import { useState, useEffect, useCallback } from 'react';
import { sortByName } from '../lib/sortByName';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
    Calculator, Star, DollarSign, TrendingUp, Loader2, Eye, Download,
    AlertCircle, MapPin, ArrowUpRight, CalendarClock, Mail, ShieldCheck,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import PdfPreviewModal from '../components/PdfPreviewModal';
import ScheduleReportModal from '../components/ScheduleReportModal';
import PdfBrandingSettings from '../components/PdfBrandingSettings';
import FreestyleInputs, { NO_PROFILE } from './salesCalc/FreestyleInputs';
import { profileLabel, profileDirty } from './salesCalc/profileUtils';
import MetricProfilesDialog from './salesCalc/MetricProfilesDialog';
import SendOnePagerDialog from './salesCalc/SendOnePagerDialog';
import { organizationsAPI, salesCalculationAPI, metricProfilesAPI } from '../lib/api';
import { useAuth } from '../lib/auth';

const ALL_LOCATIONS = '__all__';

const SECTION_KEYS = [
    'reviewsToNextTenth', 'roi', 'salesImpact', 'negativeAvoided',
    'combinedOverview', 'perLocationBreakdown',
];

const FREESTYLE_DEFAULTS = {
    prospect_name: '',
    current_rating: '4.2',
    current_reviews: '300',
    projected_reviews_per_month: '150',
    months: '3',
    annual_revenue: '',
    value_per_review: '7.50',
    sales_impact_pct_per_star: '9',
    negative_review_cost: '0',
    assumed_negative_rate_pct: '12',
    currency: 'USD',
    disclaimer: null,
};

const SalesCalculationTool = () => {
    const { t, i18n } = useTranslation();
    const { user, isSuperAdmin } = useAuth();

    const [organizations, setOrganizations] = useState([]);
    const [selectedOrg, setSelectedOrg] = useState('');
    const [locations, setLocations] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState(ALL_LOCATIONS);
    const [currency, setCurrency] = useState(null);       // null → org home currency
    const [increment, setIncrement] = useState('0.1');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [sections, setSections] = useState({
        reviewsToNextTenth: true, roi: true, salesImpact: true, negativeAvoided: true,
        combinedOverview: true, perLocationBreakdown: true,
    });
    const [pdfPreview, setPdfPreview] = useState({ open: false, loading: false, error: false, pages: [] });
    const [showSchedule, setShowSchedule] = useState(false);
    const [mode, setMode] = useState('client');           // client | freestyle
    const [fs, setFs] = useState(FREESTYLE_DEFAULTS);
    const [profiles, setProfiles] = useState([]);
    const [activeProfile, setActiveProfile] = useState(NO_PROFILE);
    const [showProfiles, setShowProfiles] = useState(false);
    const [showSend, setShowSend] = useState(false);
    // Prospect/client-specific, never an industry assumption: typed per
    // session, never stored, never part of a profile. Blank = percentage only.
    const [clientRevenue, setClientRevenue] = useState('');
    const freestyle = mode === 'freestyle';
    const revenue = parseFloat(freestyle ? fs.annual_revenue : clientRevenue) || 0;
    const loadedProfile = profiles.find((p) => p.id === activeProfile) || null;

    useEffect(() => {
        organizationsAPI.list()
            .then((res) => {
                const list = res.data || [];
                setOrganizations(sortByName(list));
                if (!selectedOrg) {
                    setSelectedOrg(isSuperAdmin() ? (list[0]?.id || '') : (user?.organization_id || list[0]?.id || ''));
                }
            })
            .catch(() => toast.error(t('salesCalc.errors.orgs', 'Could not load clients')));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!selectedOrg) return;
        setSelectedLocation(ALL_LOCATIONS);
        salesCalculationAPI.locations(selectedOrg)
            .then((res) => setLocations(res.data?.locations || []))
            .catch(() => setLocations(sortByName([])));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOrg]);

    const loadProfiles = useCallback(async () => {
        try {
            const res = await metricProfilesAPI.list();
            setProfiles(res.data?.profiles || []);
        } catch (error) {
            toast.error(t('salesCalc.profiles.errors.load', 'Could not load your profiles'));
        }
    }, [t]);

    useEffect(() => { loadProfiles(); }, [loadProfiles]);

    const fsPayload = () => ({
        current_rating: parseFloat(fs.current_rating) || 0,
        current_reviews: parseInt(fs.current_reviews, 10) || 0,
        projected_reviews_per_month: parseFloat(fs.projected_reviews_per_month) || 0,
        months: parseInt(fs.months, 10) || 1,
        value_per_review: parseFloat(fs.value_per_review) || 0,
        sales_impact_pct_per_star: parseFloat(fs.sales_impact_pct_per_star) || 0,
        negative_review_cost: parseFloat(fs.negative_review_cost) || 0,
        assumed_negative_rate_pct: parseFloat(fs.assumed_negative_rate_pct) || 0,
        target_increment: parseFloat(increment) || 0.1,
        currency: fs.currency || 'USD',
        prospect_name: fs.prospect_name || null,
        annual_revenue: revenue > 0 ? revenue : null,
        disclaimer: fs.disclaimer || null,
    });

    const runFreestyle = async () => {
        setLoading(true);
        try {
            const res = await salesCalculationAPI.freestyle(fsPayload());
            setData(res.data);
            setCurrency(res.data?.currency?.code || 'USD');
        } catch (error) {
            toast.error(error.response?.data?.detail || t('salesCalc.freestyle.errors.run', 'Could not run the projection'));
        } finally {
            setLoading(false);
        }
    };

    const loadProfileValues = (id) => {
        setActiveProfile(id);
        if (id === NO_PROFILE) {
            setFs((prev) => ({ ...prev, disclaimer: null }));
            return;
        }
        const p = profiles.find((x) => x.id === id);
        if (!p) return;
        setFs((prev) => ({
            ...prev,
            value_per_review: String(p.value_per_review ?? ''),
            sales_impact_pct_per_star: String(p.sales_impact_pct_per_star ?? ''),
            negative_review_cost: String(p.negative_review_cost ?? ''),
            assumed_negative_rate_pct: String(p.assumed_negative_rate_pct ?? ''),
            currency: p.currency || 'USD',
            // Travels with the figures onto the screen, the PDF and any copy.
            disclaimer: p.disclaimer || null,
        }));
        toast.success(t('salesCalc.profiles.loaded', 'Loaded "{{name}}" — values stay editable until you save', { name: profileLabel(p, t) }));
    };

    const load = useCallback(async () => {
        if (mode !== 'client' || !selectedOrg) return;
        setLoading(true);
        try {
            const res = await salesCalculationAPI.overview({
                organization_id: selectedOrg,
                location_ids: selectedLocation === ALL_LOCATIONS ? undefined : selectedLocation,
                target_increment: parseFloat(increment) || 0.1,
                currency: currency || undefined,
            });
            setData(res.data);
            if (!currency) setCurrency(res.data?.currency?.home_currency || 'USD');
        } catch (error) {
            toast.error(error.response?.data?.detail || t('salesCalc.errors.load', 'Could not run the calculations'));
        } finally {
            setLoading(false);
        }
    }, [mode, selectedOrg, selectedLocation, increment, currency, t]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (mode !== 'freestyle') return;
        setData(null);
        runFreestyle();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    const cur = data?.currency || {};
    const factor = Number(cur.rate_from_home) || 1;
    const money = (value) => {
        const n = Number(value);
        if (!Number.isFinite(n)) return '—';
        return `${cur.symbol || 'US$'}${(n * factor).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };
    const stars = (v) => (Number.isFinite(Number(v)) ? Number(v).toFixed(1) : '—');
    const pct = (v) => (Number.isFinite(Number(v)) ? `${Number(v) > 0 ? '+' : ''}${Number(v).toFixed(1)}%` : '—');
    const int = (v) => (Number.isFinite(Number(v)) ? Number(v).toLocaleString() : '—');
    /** Whole-dollar display for revenue-scale figures (no cents). */
    const money0 = (v) => {
        const n = Number(v);
        if (!Number.isFinite(n)) return '—';
        return `${cur.symbol || 'US$'}${Math.round(n * factor).toLocaleString()}`;
    };

    const scope = selectedLocation === ALL_LOCATIONS
        ? data?.combined
        : (data?.locations || []).find((l) => l.location_id === selectedLocation) || data?.combined;
    const blocked = scope?.state?.blocked_reason;

    const pdfLanguage = () => {
        const uiLang = (i18n.language || 'en').split('-')[0];
        return ['en', 'fr', 'es'].includes(uiLang) ? uiLang : 'en';
    };

    const pdfBody = () => (freestyle ? {
        language: pdfLanguage(),
        sections,
        freestyle: fsPayload(),
    } : {
        organization_id: selectedOrg,
        location_ids: selectedLocation === ALL_LOCATIONS ? null : [selectedLocation],
        language: pdfLanguage(),
        currency: currency || undefined,
        target_increment: parseFloat(increment) || 0.1,
        sections,
        annual_revenue: revenue > 0 ? revenue : null,
    });

    const sendOnePager = async (emails) => {
        try {
            const res = await salesCalculationAPI.sendOnePager({ ...pdfBody(), to: emails });
            toast.success(t('salesCalc.send.sent', 'One-pager sent to {{count}} recipient(s)', {
                count: res.data?.emails_sent ?? emails.length,
            }));
            return true;
        } catch (error) {
            toast.error(error.response?.data?.detail || t('salesCalc.send.failed', 'Could not send the one-pager'));
            return false;
        }
    };

    const previewOnePager = async () => {
        setPdfPreview({ open: true, loading: true, error: false, pages: [] });
        try {
            const res = await salesCalculationAPI.onePagerImages(pdfBody());
            const pages = res.data?.pages || [];
            setPdfPreview({ open: true, loading: false, error: !pages.length, pages });
        } catch (error) {
            setPdfPreview({ open: true, loading: false, error: true, pages: [] });
        }
    };

    const downloadOnePager = async () => {
        setGenerating(true);
        try {
            const res = await salesCalculationAPI.onePagerPdf(pdfBody());
            const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `YulOnTap_Review_Calculations_${data?.report_info?.end_date || 'report'}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success(t('salesCalc.toasts.downloaded', 'One-pager downloaded'));
        } catch (error) {
            toast.error(t('salesCalc.errors.pdf', 'Could not generate the one-pager'));
        } finally {
            setGenerating(false);
        }
    };

    const pickCurrency = (code) => {
        setCurrency(code);
        if (freestyle) setFs((p) => ({ ...p, currency: code }));
    };

    const StatCard = ({ testid, icon: Icon, tone, value, label, note, sub, calculable }) => (
        <div className={`rounded-xl border p-4 ${calculable ? tone : 'border-border bg-muted/40'}`} data-testid={testid}>
            <div className="flex items-center gap-2 text-muted-foreground">
                <Icon className="w-4 h-4" />
                <span className="text-xs uppercase tracking-wide">{label}</span>
            </div>
            <p className={`mt-2 font-bold ${calculable ? 'text-3xl text-slate-900 dark:text-white' : 'text-base text-muted-foreground'}`} data-testid={`${testid}-value`}>
                {value}
            </p>
            {sub ? (
                <p className="mt-1 text-sm font-semibold text-slate-700 dark:text-slate-200" data-testid={`${testid}-sub`}>
                    {sub}
                </p>
            ) : null}
            {note ? <p className="mt-1.5 text-xs text-muted-foreground">{note}</p> : null}
        </div>
    );

    return (
        <div className="space-y-6" data-testid="sales-calc-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2 text-slate-900 dark:text-white"
                        data-insight-id="sales-calculation-tool.overview" data-insight-primary="true">
                        <Calculator className="w-6 h-6 text-emerald-600" />
                        {t('salesCalc.title', 'Sales/Calculation Tool')}
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        {t('salesCalc.subtitle', 'Reviews to the next star, value earned since baseline, and estimated sales impact.')}
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Button variant="outline" onClick={() => setShowSchedule(true)} disabled={!selectedOrg || freestyle} data-testid="calc-schedule-btn">
                        <CalendarClock className="w-4 h-4 mr-1.5" />{t('salesCalc.schedule', 'Schedule')}
                    </Button>
                    <Button variant="outline" onClick={previewOnePager} disabled={loading || !data} data-testid="calc-preview-btn">
                        <Eye className="w-4 h-4 mr-1.5" />{t('salesCalc.preview', 'Preview one-pager')}
                    </Button>
                    <Button variant="outline" onClick={() => setShowSend(true)} disabled={loading || !data} data-testid="calc-send-btn">
                        <Mail className="w-4 h-4 mr-1.5" />{t('salesCalc.send.button', 'Send via email')}
                    </Button>
                    <Button onClick={downloadOnePager} disabled={generating || !data} data-testid="calc-download-btn">
                        {generating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
                        {t('salesCalc.generate', 'Generate one-pager')}
                    </Button>
                </div>
            </div>

            <Card>
                <CardHeader className="pb-3">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                            <CardTitle className="text-base">{t('salesCalc.scopeTitle', 'What are we calculating?')}</CardTitle>
                            <CardDescription>
                                {freestyle
                                    ? t('salesCalc.mode.freestyleHint', 'Type the numbers in — nothing is read from or written to a client record.')
                                    : t('salesCalc.mode.clientHint', 'Pick a client, then one location or all locations combined.')}
                            </CardDescription>
                        </div>
                        <div className="flex items-center gap-1.5" data-testid="calc-mode-toggle">
                            {[['client', t('salesCalc.mode.client', 'Client')],
                              ['freestyle', t('salesCalc.mode.freestyle', 'Prospect / Freestyle')]].map(([key, label]) => (
                                <Button key={key} type="button" size="sm"
                                    variant={mode === key ? 'default' : 'outline'}
                                    onClick={() => setMode(key)}
                                    data-testid={`calc-mode-${key}`}>
                                    {label}
                                </Button>
                            ))}
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-4">
                    {!freestyle ? (
                        <>
                            <div className="space-y-1.5">
                                <Label>{t('salesCalc.client', 'Client')}</Label>
                                <Select value={selectedOrg} onValueChange={setSelectedOrg}>
                                    <SelectTrigger data-testid="calc-org-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {organizations.map((o) => (
                                            <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <Label>{t('salesCalc.location', 'Location')}</Label>
                                <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                                    <SelectTrigger data-testid="calc-location-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value={ALL_LOCATIONS}>{t('salesCalc.allLocations', 'All locations combined')}</SelectItem>
                                        {locations.map((l) => (
                                            <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </>
                    ) : null}
                    <div className="space-y-1.5">
                        <Label>{t('salesCalc.increment', 'Rating target')}</Label>
                        <Select value={increment} onValueChange={setIncrement}>
                            <SelectTrigger data-testid="calc-increment-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="0.1">{t('salesCalc.incNext', 'Next +0.1★')}</SelectItem>
                                <SelectItem value="0.2">+0.2★</SelectItem>
                                <SelectItem value="0.5">+0.5★</SelectItem>
                                <SelectItem value="1">+1.0★</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5">
                        <Label>{t('salesCalc.currency', 'Currency')}</Label>
                        <div className="flex gap-1.5" data-testid="calc-currency-toggle">
                            {['USD', 'CAD'].map((code) => (
                                <Button key={code} type="button" size="sm"
                                    variant={(currency || cur.home_currency) === code ? 'default' : 'outline'}
                                    onClick={() => pickCurrency(code)}
                                    data-testid={`calc-currency-${code}`}>
                                    {code}
                                </Button>
                            ))}
                        </div>
                        {cur.converted ? (
                            <p className="text-[11px] text-muted-foreground" data-testid="calc-currency-caption">
                                {t('salesCalc.convertedAt', 'Converted at 1 USD = {{rate}} CAD (stored rate)', { rate: cur.usd_to_cad_rate })}
                            </p>
                        ) : null}
                    </div>
                    {!freestyle ? (
                        <div className="space-y-1.5">
                            <Label htmlFor="calc-client-revenue">{t('salesCalc.annualRevenue', 'Annual revenue (optional)')}</Label>
                            <Input id="calc-client-revenue" type="number" min="0" step="1000" placeholder="—"
                                value={clientRevenue}
                                onChange={(e) => setClientRevenue(e.target.value)}
                                data-testid="calc-client-revenue" />
                            <p className="text-[11px] text-muted-foreground">
                                {t('salesCalc.annualRevenueHint', 'Turns the sales-impact percentage into a dollar figure. Blank = percentage only.')}
                            </p>
                        </div>
                    ) : null}
                </CardContent>
            </Card>

            {freestyle ? (
                <FreestyleInputs
                    value={fs}
                    onChange={setFs}
                    profiles={profiles}
                    activeProfile={activeProfile}
                    onLoadProfile={loadProfileValues}
                    onSaveAs={() => setShowProfiles(true)}
                    onManage={() => setShowProfiles(true)}
                    onRun={runFreestyle}
                    loading={loading}
                />
            ) : null}

            {loading && !data ? (
                <div className="flex items-center gap-2 text-muted-foreground" data-testid="calc-loading">
                    <Loader2 className="w-4 h-4 animate-spin" />{t('common.loading', 'Loading…')}
                </div>
            ) : null}

            {data ? (
                <>
                    <Card data-testid="calc-snapshot">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-base flex items-center gap-2">
                                <MapPin className="w-4 h-4" />
                                {selectedLocation === ALL_LOCATIONS
                                    ? t('salesCalc.allLocations', 'All locations combined')
                                    : scope?.location_name}
                            </CardTitle>
                            <CardDescription>
                                {t('salesCalc.baselineToToday', 'Baseline {{from}} → today {{to}}', {
                                    from: scope?.baseline?.baseline_date || data.report_info?.start_date || '—',
                                    to: data.report_info?.end_date,
                                })}
                                {scope?.days_since_baseline != null
                                    ? ` · ${t('salesCalc.daysSince', '{{n}} days', { n: scope.days_since_baseline })}`
                                    : ''}
                                {data.projection ? (
                                    <span className="block mt-1 text-emerald-700 dark:text-emerald-400" data-testid="calc-projection-caption">
                                        {t('salesCalc.freestyle.projection', '{{perMonth}} reviews/month for {{months}} months = {{total}} new reviews', {
                                            perMonth: int(data.projection.reviews_per_month),
                                            months: data.projection.months,
                                            total: int(data.projection.projected_reviews),
                                        })}
                                        {` · ${t('salesCalc.freestyle.projectedRating', 'Projected rating')}: ${stars(data.projection.projected_rating)}★`}
                                    </span>
                                ) : null}
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="grid gap-4 sm:grid-cols-4">
                            <div><p className="text-xs text-muted-foreground uppercase">{t('salesCalc.baselineRating', 'Baseline rating')}</p>
                                <p className="text-xl font-semibold text-slate-900 dark:text-white" data-testid="calc-baseline-rating">{stars(scope?.baseline?.google_rating)}★</p></div>
                            <div><p className="text-xs text-muted-foreground uppercase">{t('salesCalc.currentRating', 'Current rating')}</p>
                                <p className="text-xl font-semibold text-slate-900 dark:text-white" data-testid="calc-current-rating">{stars(scope?.current?.google_rating)}★</p></div>
                            <div><p className="text-xs text-muted-foreground uppercase">{t('salesCalc.reviewsNow', 'Reviews now')}</p>
                                <p className="text-xl font-semibold text-slate-900 dark:text-white" data-testid="calc-current-reviews">{int(scope?.current?.google_reviews)}</p></div>
                            <div><p className="text-xs text-muted-foreground uppercase">{t('salesCalc.reviewsEarned', 'Reviews earned')}</p>
                                <p className="text-xl font-semibold text-slate-900 dark:text-white" data-testid="calc-reviews-earned">{int(scope?.new_reviews_since_baseline)}</p></div>
                        </CardContent>
                    </Card>

                    {blocked ? (
                        <Card className="border-amber-400/60 bg-amber-50 dark:bg-amber-950/20" data-testid="calc-empty-state">
                            <CardContent className="pt-5 space-y-2">
                                <p className="flex items-center gap-2 font-medium text-amber-700 dark:text-amber-300">
                                    <AlertCircle className="w-4 h-4" />
                                    {blocked === 'baseline_not_set'
                                        ? t('salesCalc.blocked.baseline', 'No baseline is set for this scope, so growth cannot be calculated.')
                                        : t('salesCalc.blocked.snapshot', 'No current snapshot exists for this scope, so growth cannot be calculated.')}
                                </p>
                                <Link to="/locations" className="inline-flex items-center gap-1.5 text-sm text-emerald-700 dark:text-emerald-400 underline" data-testid="calc-fix-link">
                                    {blocked === 'baseline_not_set'
                                        ? t('salesCalc.blocked.baselineCta', 'Open Locations → Quick Set Baselines')
                                        : t('salesCalc.blocked.snapshotCta', "Open Locations → update today's snapshot")}
                                    <ArrowUpRight className="w-3.5 h-3.5" />
                                </Link>
                            </CardContent>
                        </Card>
                    ) : null}

                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        <StatCard
                            testid="calc-card-reviews-needed"
                            icon={Star}
                            tone="border-blue-500/40 bg-blue-500/5"
                            calculable={!!scope?.reviews_to_next?.calculable}
                            value={scope?.reviews_to_next?.calculable
                                ? int(scope.reviews_to_next.reviews_needed)
                                : t('salesCalc.notCalculable', 'Not calculable yet')}
                            label={t('salesCalc.cards.reviewsNeeded', 'Reviews needed for {{target}}★', {
                                target: stars(scope?.reviews_to_next?.target_rating),
                            })}
                            note={scope?.reviews_to_next?.calculable
                                ? t('salesCalc.cards.reviewsNeededNote', 'From {{current}}★ on {{count}} reviews, assuming new reviews are 5★', {
                                    current: stars(scope.reviews_to_next.current_rating),
                                    count: int(scope.reviews_to_next.current_reviews),
                                })
                                : (scope?.reviews_to_next?.reason === 'target_unreachable'
                                    ? t('salesCalc.cards.unreachable', 'Already at or near 5.0★ — not reachable with 5★ reviews alone')
                                    : t('salesCalc.cards.missing', 'Set the baseline and a current snapshot first'))}
                        />
                        <StatCard
                            testid="calc-card-roi"
                            icon={DollarSign}
                            tone="border-emerald-500/40 bg-emerald-500/5"
                            calculable={!!scope?.roi?.calculable}
                            value={scope?.roi?.calculable ? money(scope.roi.amount) : t('salesCalc.notCalculable', 'Not calculable yet')}
                            label={t('salesCalc.cards.roi', 'Value of reviews earned')}
                            note={scope?.roi?.calculable
                                ? t('salesCalc.cards.roiNote', '{{earned}} reviews × {{value}} per review', {
                                    earned: int(scope.roi.reviews_earned), value: money(scope.roi.value_per_review),
                                })
                                : t('salesCalc.cards.missing', 'Set the baseline and a current snapshot first')}
                        />
                        <StatCard
                            testid="calc-card-sales-impact"
                            icon={TrendingUp}
                            tone="border-violet-500/40 bg-violet-500/5"
                            calculable={!!scope?.sales_impact?.calculable}
                            value={scope?.sales_impact?.calculable ? pct(scope.sales_impact.pct) : t('salesCalc.notCalculable', 'Not calculable yet')}
                            label={t('salesCalc.cards.salesImpact', 'Estimated sales impact')}
                            sub={scope?.sales_impact?.calculable && revenue > 0
                                ? t('salesCalc.cards.salesImpactAmount', '≈ {{amount}} / year on {{revenue}} revenue', {
                                    amount: money0(revenue * (Number(scope.sales_impact.pct) / 100)),
                                    revenue: money0(revenue),
                                })
                                : null}
                            note={scope?.sales_impact?.calculable
                                ? t('salesCalc.cards.salesImpactNote', '{{delta}}★ change × {{per}}% per star', {
                                    delta: `${Number(scope.sales_impact.rating_delta) > 0 ? '+' : ''}${stars(scope.sales_impact.rating_delta)}`,
                                    per: scope.sales_impact.pct_per_star,
                                })
                                : t('salesCalc.cards.missing', 'Set the baseline and a current snapshot first')}
                        />
                        <StatCard
                            testid="calc-card-negative-avoided"
                            icon={ShieldCheck}
                            tone="border-amber-500/40 bg-amber-500/5"
                            calculable={!!scope?.negative_avoided?.calculable}
                            value={scope?.negative_avoided?.calculable
                                ? money(scope.negative_avoided.amount)
                                : t('salesCalc.notCalculable', 'Not calculable yet')}
                            label={t('salesCalc.cards.negativeAvoided', 'Negative-review cost avoided')}
                            note={scope?.negative_avoided?.calculable
                                ? t('salesCalc.cards.negativeAvoidedNote', '{{avoided}} negative reviews avoided ({{rate}}% of new reviews) × {{cost}} each', {
                                    avoided: scope.negative_avoided.reviews_avoided,
                                    rate: scope.negative_avoided.assumed_negative_rate_pct,
                                    cost: money(scope.negative_avoided.negative_review_cost),
                                })
                                : (scope?.negative_avoided?.reason === 'cost_not_configured'
                                    ? t('salesCalc.cards.negativeMissing', 'Set a cost per negative review to see this figure')
                                    : t('salesCalc.cards.missing', 'Set the baseline and a current snapshot first'))}
                        />
                    </div>

                    {(data.locations || []).length > 1 ? (
                        <Card data-testid="calc-per-location">
                            <CardHeader className="pb-3">
                                <CardTitle className="text-base">{t('salesCalc.byLocation', 'By location')}</CardTitle>
                            </CardHeader>
                            <CardContent className="overflow-x-auto">
                                <table className="w-full text-sm text-slate-900 dark:text-slate-100">
                                    <thead className="text-xs uppercase text-muted-foreground">
                                        <tr className="text-left">
                                            <th className="py-2">{t('salesCalc.location', 'Location')}</th>
                                            <th className="py-2">{t('salesCalc.table.rating', 'Rating')}</th>
                                            <th className="py-2 text-right">{t('salesCalc.reviewsNow', 'Reviews now')}</th>
                                            <th className="py-2 text-right">{t('salesCalc.reviewsEarned', 'Reviews earned')}</th>
                                            <th className="py-2 text-right">{t('salesCalc.table.toNext', 'To next ★')}</th>
                                            <th className="py-2 text-right">{t('salesCalc.table.value', 'Value')}</th>
                                            <th className="py-2 text-right">{t('salesCalc.table.impact', 'Impact')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.locations.map((l) => (
                                            <tr key={l.location_id} className="border-t" data-testid={`calc-row-${l.location_id}`}>
                                                <td className="py-2">{l.location_name}</td>
                                                <td className="py-2">{stars(l.baseline?.google_rating)} → {stars(l.current?.google_rating)}</td>
                                                <td className="py-2 text-right">{int(l.current?.google_reviews)}</td>
                                                <td className="py-2 text-right">{int(l.new_reviews_since_baseline)}</td>
                                                <td className="py-2 text-right">{l.reviews_to_next?.calculable ? int(l.reviews_to_next.reviews_needed) : '—'}</td>
                                                <td className="py-2 text-right font-medium">{l.roi?.calculable ? money(l.roi.amount) : '—'}</td>
                                                <td className="py-2 text-right">{l.sales_impact?.calculable ? pct(l.sales_impact.pct) : '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </CardContent>
                        </Card>
                    ) : null}

                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-base">{t('salesCalc.sectionsTitle', 'One-pager sections')}</CardTitle>
                            <CardDescription>{t('salesCalc.sectionsHint', 'Turn any block off — the one-pager reflows around it.')}</CardDescription>
                        </CardHeader>
                        <CardContent className="flex flex-wrap gap-4">
                            {SECTION_KEYS.map((key) => (
                                <label key={key} className="flex items-center gap-2 text-sm cursor-pointer">
                                    <Checkbox
                                        checked={sections[key] !== false}
                                        disabled={key === 'combinedOverview' && (data.locations || []).length <= 1}
                                        onCheckedChange={(checked) => setSections((p) => ({ ...p, [key]: !!checked }))}
                                        data-testid={`calc-section-${key}`}
                                    />
                                    <span className="text-slate-900 dark:text-slate-100">{t(`salesCalc.sections.${key}`, key)}</span>
                                </label>
                            ))}
                        </CardContent>
                    </Card>

                    <PdfBrandingSettings selectedOrg={freestyle ? null : selectedOrg} />

                    <p className="text-xs text-muted-foreground" data-testid="calc-assumptions">
                        {t('salesCalc.assumptions', 'New reviews are assumed to be 5★. Sales impact uses {{per}}% per full star. Per-review value comes from this client\'s configured platform values ({{value}}).', {
                            per: data.assumptions?.sales_impact_pct_per_star,
                            value: money(data.assumptions?.value_per_review),
                        })}
                    </p>
                </>
            ) : null}

            <ScheduleReportModal
                open={showSchedule}
                onOpenChange={setShowSchedule}
                organizations={organizations}
                isSuper={isSuperAdmin()}
                reportKey="review_calculations_onepager"
                inherited={{ selectedOrg, sections }}
                editing={null}
                onSaved={() => setShowSchedule(false)}
            />

            <MetricProfilesDialog
                open={showProfiles}
                onOpenChange={setShowProfiles}
                profiles={profiles}
                currentValues={fs}
                suggestedName={loadedProfile && profileDirty(loadedProfile, fs)
                    ? `${profileLabel(loadedProfile, t)} (${t('salesCalc.profiles.copySuffix', 'custom')})`
                    : ''}
                onChanged={loadProfiles}
            />

            <SendOnePagerDialog
                open={showSend}
                onOpenChange={setShowSend}
                onSend={sendOnePager}
            />

            <PdfPreviewModal
                open={pdfPreview.open}
                onOpenChange={(open) => setPdfPreview((p) => ({ ...p, open }))}
                title={t('salesCalc.previewTitle', 'Review calculations one-pager')}
                loading={pdfPreview.loading}
                error={pdfPreview.error}
                pages={pdfPreview.pages}
                onRetry={previewOnePager}
            />
        </div>
    );
};

export default SalesCalculationTool;
